import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useText } from "../context/ContentContext";

const routes = [
    {
        from: "Ontario",
        to: "New Brunswick",
        slug: "moving-from-ontario-to-new-brunswick-in-2026-what-buyers-need-to-know",
        stat: "Save 60%+ on home price",
        blurb:
            "NB average home ~$341K vs. Ontario ~$812K. One of Canada's top inbound provinces in 2025.",
    },
    {
        from: "Ontario",
        to: "Nova Scotia",
        slug: "ontario-to-nova-scotia-the-halifax-move-that-keeps-topping-the-migration-charts",
        stat: "8,693 Ontarians moved in 2025",
        blurb:
            "Ontario remains Nova Scotia's #1 source of interprovincial movers for the fifth year running.",
    },
    {
        from: "British Columbia",
        to: "Alberta",
        slug: "bc-to-alberta-in-2026-why-british-columbians-keep-choosing-calgary-and-edmonton",
        stat: "No PST · No land transfer tax",
        blurb:
            "Alberta led every province in net interprovincial migration in 2025. Calgary benchmark ~$571K vs. Vancouver ~$1.18M.",
    },
    {
        from: "Toronto / GTA",
        to: "Prince Edward County",
        slug: "toronto-to-prince-edward-county-the-in-province-move-that-keeps-surprising-gta-sellers",
        stat: "2.5 hours from Toronto",
        blurb:
            "Wine country, beaches, and a real downshift — without leaving Ontario. Median sold price ~$557–$615K.",
    },
];

export default function PopularMoves() {
    const title = useText(
        "popular_moves_title",
        "Popular Moves Across Canada"
    );
    const subtitle = useText(
        "popular_moves_subtitle",
        "The interprovincial routes Canadians are actually taking in 2026 — with the data, the cost gap, and the local pitfalls explained."
    );

    return (
        <section
            className="bg-brand-sand/30 border-y border-brand-line"
            data-testid="popular-moves-section"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
                <div className="max-w-2xl mb-16">
                    <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
                        — Popular Moves
                    </div>
                    <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-brand-ink">
                        {title}
                    </h2>
                    <p className="mt-6 text-brand-muted leading-relaxed">
                        {subtitle}
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-px bg-brand-line border border-brand-line">
                    {routes.map((route, i) => (
                        <motion.div
                            key={route.slug}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.55, delay: i * 0.1 }}
                            className="bg-brand-paper group"
                            data-testid={`popular-move-card-${route.slug}`}
                        >
                            <Link
                                to={`/journal/${route.slug}`}
                                className="block p-10 lg:p-12 hover:bg-brand-sand/40 transition-colors h-full"
                            >
                                <div className="flex items-center gap-3 text-brand-ink font-serif text-2xl lg:text-3xl leading-tight mb-5">
                                    <span>{route.from}</span>
                                    <ArrowRight
                                        size={22}
                                        strokeWidth={1.5}
                                        className="text-brand-gold mt-1 transition-transform group-hover:translate-x-1"
                                    />
                                    <span className="italic">{route.to}</span>
                                </div>
                                <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold mb-5">
                                    {route.stat}
                                </div>
                                <p className="text-brand-muted leading-relaxed mb-8">
                                    {route.blurb}
                                </p>
                                <div className="text-sm tracking-wide text-brand-ink inline-flex items-center gap-2 transition-all group-hover:gap-3">
                                    Read the route guide{" "}
                                    <span aria-hidden>→</span>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>

                <div className="mt-12 flex flex-wrap items-center gap-x-2 gap-y-3 text-brand-muted">
                    <span className="text-sm">Don't see your route?</span>
                    <Link
                        to="/get-started"
                        data-testid="popular-moves-cta-link"
                        className="text-sm font-semibold text-brand-ink underline underline-offset-4 hover:text-brand-gold transition-colors"
                    >
                        Get Matched Now! →
                    </Link>
                </div>
            </div>
        </section>
    );
}
